import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  Card,
  Box,
  Typography,
  Chip,
  IconButton,
  Divider,
  DialogActions,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import QRCode from "react-qr-code";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@store/store";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MuiButton from "@components/Button/MuiButton";
import { getFileSignedURLMorthThunk as getFileSignedURLMorthAccessThunk } from "@modules/stepper/redux/thunk/documentThunk";
import { getFileSignedURLMorthThunk as getFileSignedURLMorthROWThunk } from "@modules/stepper/redux/thunk/documentThunk";
import { getSignedPreviewUrl } from "../../../utils/getViewSignedUrlHandler";

interface Props {
  open: boolean;
  onClose: () => void;
  licenseURL?: any;
  licenseType: "provisional" | "final" | "renewal";
  moduleType: "access" | "row";
}

const QRLicenseGeneratorModal: React.FC<Props> = ({
  open,
  onClose,
  licenseURL,
  licenseType,
  moduleType,
}) => {
  const location = useLocation();
  const qrCodeRef = React.useRef<HTMLDivElement>(null);
  const [qrURL, setQrURL] = useState<string>("");
  const dispatch = useDispatch();

  const applicationDetailsROW = useSelector(
    (state: RootState) => state?.applicationDetailsROW?.applicationDetailsROW,
  );

  const applicationDetails = useSelector(
    (state: RootState) => state?.applicationDetails?.applicationDetails,
  );

  const prevOpenRef = useRef(false);

  useEffect(() => {
    if (!prevOpenRef.current && open && licenseURL) {
      generateSignedUrl(licenseURL);
    }
    prevOpenRef.current = open;
  }, [open, licenseURL]);

  if (!applicationDetails && !applicationDetailsROW) return;

  const isRowRoute = location.pathname.includes("row");
  const details = isRowRoute ? applicationDetailsROW : applicationDetails;

  if (!details) return null;

  const {
    loi_name,
    loi_dealer_name,
    provisional_license_date,
    final_license_date,
    tat_info,
    user,
    organizations,
    orgnizations,
    org_name,
    application_name,
    renewal_license_date,
  } = details;

  // const qrURL =
  //   process.env.REACT_APP_API_IMAGE_URL +
  //   "morth-documents/access/" +
  //   licenseURL;

  const generateSignedUrl = async (doc_name: string) => {
    try {
      // const signedUrl = await getSignedPreviewUrl({
      //   document_name: doc_name,
      //   dispatch,
      //   getSignedUrlThunk: isRowRoute
      //     ? getFileSignedURLMorthROWThunk
      //     : getFileSignedURLMorthAccessThunk,
      // });
      const publicUrl = `${process.env.REACT_APP_API_BASE_URL}/backend/api/file/public-view-license?fileName=${licenseURL}&moduletype=${moduleType}`;
      if (!publicUrl) return;

      setQrURL(publicUrl);
    } catch (e) {
      console.error(e);
    }
  };

  const getExpiryDate = (startDateStr: string, monthsToAdd: number) => {
    const startDate = new Date(startDateStr);
    const expiryDate = new Date(startDate);
    expiryDate.setMonth(expiryDate.getMonth() + monthsToAdd);

    return expiryDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleDownloadQR = async () => {
    if (!qrCodeRef.current) return;

    const html2canvas = (await import("html2canvas")).default;
    const jsPDF = (await import("jspdf")).default;

    const canvas = await html2canvas(qrCodeRef.current, {
      scale: 2,
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
    pdf.save("QRCode.pdf");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "none",
          height: "90vh", // important for footer sticking
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Close Icon */}
      <IconButton
        onClick={onClose}
        sx={{
          position: "absolute",
          top: 6,
          right: 6,
          zIndex: 2,
          color: "#757575",
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* SCROLLABLE CONTENT */}
      <DialogContent
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          p: 0,
        }}
      >
        <Card
          ref={qrCodeRef}
          elevation={0}
          sx={{
            width: "100%",
            borderRadius: "12px",
            p: 3,
            textAlign: "center",
          }}
        >
          {/* Header */}
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
            mb={0.5}
          >
            <Typography fontSize={18} fontWeight={700}>
              {licenseType === "provisional"
                ? "Provisional NOC"
                : licenseType === "renewal"
                  ? "Renewal NOC"
                  : "Final NOC"}
            </Typography>
            <Chip
              label="Authorised"
              size="small"
              sx={{
                backgroundColor: "#E8F5E9",
                color: "#2E7D32",
                fontWeight: 500,
                fontSize: 11,
              }}
            />
          </Box>

          <Typography fontSize={24} fontWeight={700} mb={2}>
            {organizations?.[0]?.business_name ||
              orgnizations?.[0]?.business_name ||
              user?.name ||
              application_name}
          </Typography>

          {/* QR */}
          <Box display="flex" justifyContent="center" mb={3}>
            <QRCode value={qrURL} size={320} />
          </Box>

          {/* Info Grid */}
          <Box
            display="grid"
            gridTemplateColumns="1fr 1fr"
            columnGap={3}
            rowGap={2}
            textAlign="left"
          >
            {loi_name && loi_dealer_name && (
              <>
                <Box>
                  <Typography fontSize={12} color="gray" mb={0.3}>
                    Name of the Oil Company
                  </Typography>
                  <Typography fontSize={14} fontWeight={600}>
                    {loi_name}
                  </Typography>
                </Box>

                <Box textAlign="right">
                  <Typography fontSize={12} color="gray" mb={0.3}>
                    LOI Holder Name
                  </Typography>
                  <Typography fontSize={14} fontWeight={600}>
                    {loi_dealer_name}
                  </Typography>
                </Box>
              </>
            )}

            <Box>
              <Typography fontSize={12} color="gray" mb={0.3}>
                {licenseType === "provisional"
                  ? "Provisional NOC Issue Date"
                  : licenseType === "renewal"
                    ? "Renewal NOC Issue Date"
                    : "Final NOC Issue Date"}
              </Typography>
              <Typography fontSize={14} fontWeight={600}>
                {licenseType === "provisional"
                  ? dayjs(provisional_license_date).format("DD MMMM YYYY")
                  : licenseType === "renewal"
                    ? dayjs(renewal_license_date).format("DD MMMM YYYY")
                    : dayjs(final_license_date).format("DD MMMM YYYY")}
              </Typography>
            </Box>

            <Box textAlign="right">
              <Typography fontSize={12} color="gray" mb={0.3}>
                NOC Validity Date
              </Typography>
              <Typography fontSize={14} fontWeight={600}>
                {licenseType === "final"
                  ? dayjs(
                      getExpiryDate(final_license_date, isRowRoute ? 60 : 180),
                    ).format("DD MMMM YYYY")
                  : licenseType === "renewal"
                    ? dayjs(
                        getExpiryDate(
                          renewal_license_date,
                          isRowRoute ? 60 : 180,
                        ),
                      ).format("DD MMMM YYYY")
                    : dayjs(getExpiryDate(provisional_license_date, 12)).format(
                        "DD MMMM YYYY",
                      )}
              </Typography>
            </Box>
          </Box>

          {/* Footer Note */}
          <Box sx={{ mt: 2 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography
              variant="caption"
              align="center"
              sx={{
                display: "block",
                color: "#9E9E9E",
                fontSize: "12px",
                lineHeight: 1.4,
              }}
            >
              This is a system-generated document. No physical signature is
              required. For verification of the authenticity of this document,
              kindly scan the QR Code.
            </Typography>
          </Box>
        </Card>
      </DialogContent>

      {/* STICKY FOOTER */}
      <DialogActions
        sx={{
          borderTop: "1px solid #E7E6EB",
          px: 2,
          py: 2,
          gap: 1.5,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <MuiButton
          name="Download"
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={handleDownloadQR}
          sx={{
            borderRadius: "50px",
            background: "#3A1C73",
            color: "#fff",
            border: "1px solid #3A1C73",
            padding: "6px 10px", // balanced vertical & horizontal padding
            minWidth: "120px", // consistent width
            fontWeight: 500,
            textTransform: "none",
            "&:hover": {
              background: "#2c1559",
            },
          }}
        >
          Download
        </MuiButton>

        <MuiButton
          name="Close"
          onClick={onClose}
          sx={{
            borderRadius: "50px",
            border: "1px solid #3A1C73",
            color: "#3A1C73",
            padding: "6px 10px", // balanced vertical & horizontal padding
            minWidth: "120px", // consistent width
            fontWeight: 500,
            textTransform: "none",
            "&:hover": {
              background: "#3A1C73",
              color: "#fff",
            },
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default QRLicenseGeneratorModal;
